import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useLMS } from '../../context/LMSContext';
import { db } from '../../lib/supabase';
import { jsPDF } from 'jspdf';

// Local draft storage for in-progress answers.
//
// A reflection can be twenty minutes of writing, and anything that interrupts
// the tab before Save - a crash, a closed laptop, the inactivity logout - used
// to take all of it. Drafts are keyed per student and question so a shared
// computer never shows one student another's words, and they are cleared the
// moment the answer reaches the server.
const DRAFT_PREFIX = 'lms_reflection_draft';
const DRAFT_DEBOUNCE_MS = 800;

const draftKey = (studentId, questionId) => `${DRAFT_PREFIX}:${studentId}:${questionId}`;

// Every access is wrapped: private windows, blocked site data, and full quotas
// all throw, and none of them should break the page.
function readDraft(studentId, questionId) {
  try {
    const raw = window.localStorage.getItem(draftKey(studentId, questionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.text !== 'string') return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeDraft(studentId, questionId, text) {
  try {
    window.localStorage.setItem(
      draftKey(studentId, questionId),
      JSON.stringify({ text, savedAt: new Date().toISOString() })
    );
    return true;
  } catch (_) {
    return false;
  }
}

function clearDraft(studentId, questionId) {
  try {
    window.localStorage.removeItem(draftKey(studentId, questionId));
  } catch (_) {
    /* nothing to clear */
  }
}

// A draft wins only when the server has nothing, or when it was typed after
// whatever the server last stored.
function draftIsNewer(draft, serverText, serverUpdatedAt) {
  if (!draft) return false;
  if (draft.text === serverText) return false;
  if (!serverText) return true;
  const draftAt = Date.parse(draft.savedAt);
  const serverAt = Date.parse(serverUpdatedAt);
  if (Number.isNaN(draftAt)) return false;
  if (Number.isNaN(serverAt)) return true;
  return draftAt > serverAt;
}

/**
 * StudentReflections – Student view for answering weekly reflection questions.
 * Shows questions for a given module (week) and lets students save responses.
 */
const StudentReflections = ({ moduleId, weekNumber, moduleTitle }) => {
  const { currentUser, getCohortById, modules } = useLMS();

  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [draftMessage, setDraftMessage] = useState('');

  const draftTimerRef = useRef(null);
  const draftMessageTimerRef = useRef(null);
  const pendingDraftsRef = useRef({});
  const flushDraftsRef = useRef(() => {});

  const cohort = getCohortById(currentUser?.cohortId);

  // moduleId -> { weekNumber, title } so the PDF can label each section with
  // the week it came from instead of a generic heading.
  const moduleLookup = useMemo(() => {
    const map = {};
    (modules || []).forEach(m => {
      map[m.id] = {
        weekNumber: m.weekNumber || m.week_number,
        title: m.title || ''
      };
    });
    return map;
  }, [modules]);

  useEffect(() => {
    if (moduleId && currentUser?.id) loadData();
  }, [moduleId, currentUser?.id]);

  const loadData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [qRes, rRes] = await Promise.all([
        db.getReflectionQuestions(moduleId),
        db.getStudentReflectionResponses(currentUser.id)
      ]);

      if (qRes.error) throw qRes.error;
      if (rRes.error) throw rRes.error;

      const loadedQuestions = qRes.data || [];
      setQuestions(loadedQuestions);

      // questionId -> what the server has, with the time it was stored
      const server = {};
      (rRes.data || []).forEach(r => {
        server[r.question_id] = {
          text: r.response_text || '',
          updatedAt: r.updated_at || r.submitted_at || null
        };
      });

      // Start from the server copy, then let a newer local draft win.
      const map = {};
      let restored = 0;
      loadedQuestions.forEach(q => {
        const serverText = server[q.id]?.text || '';
        const draft = readDraft(currentUser.id, q.id);

        if (draftIsNewer(draft, serverText, server[q.id]?.updatedAt)) {
          map[q.id] = draft.text;
          restored += 1;
        } else {
          map[q.id] = serverText;
          // The server has caught up with this draft, so stop keeping it.
          if (draft) clearDraft(currentUser.id, q.id);
        }
      });

      setResponses(map);
      if (restored > 0) {
        setDraftMessage(`Restored ${restored} unsaved answer${restored === 1 ? '' : 's'}`);
      }
    } catch (err) {
      console.error('Error loading reflections:', err);
      Sentry.captureException(err);
      setLoadError(err.message || String(err));
    }
    setLoading(false);
  };

  const showDraftMessage = useCallback((text) => {
    setDraftMessage(text);
    if (draftMessageTimerRef.current) clearTimeout(draftMessageTimerRef.current);
    draftMessageTimerRef.current = setTimeout(() => setDraftMessage(''), 2500);
  }, []);

  // Write whatever has been typed since the last flush. Empty answers drop
  // their draft rather than storing a blank one.
  const flushDrafts = useCallback((notify = true) => {
    const pending = pendingDraftsRef.current;
    pendingDraftsRef.current = {};

    const ids = Object.keys(pending);
    if (ids.length === 0 || !currentUser?.id) return;

    let wrote = false;
    ids.forEach(questionId => {
      const text = pending[questionId];
      if (text && text.trim()) {
        if (writeDraft(currentUser.id, questionId, text)) wrote = true;
      } else {
        clearDraft(currentUser.id, questionId);
      }
    });

    // Silent when storage is unavailable: the answer is still in the textarea,
    // and claiming "Draft saved" when nothing was stored would be a lie.
    // notify=false on unmount: the component is gone, so there is nothing to
    // show and no reason to leave a timer behind.
    if (wrote && notify) showDraftMessage('Draft saved');
  }, [currentUser?.id, showDraftMessage]);

  // Keep a ref so the unmount cleanup below always flushes the latest version.
  useEffect(() => {
    flushDraftsRef.current = flushDrafts;
  }, [flushDrafts]);

  useEffect(() => {
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      if (draftMessageTimerRef.current) clearTimeout(draftMessageTimerRef.current);
      // Navigating away mid-sentence should still leave a draft behind.
      flushDraftsRef.current(false);
    };
  }, []);

  const handleResponseChange = (questionId, text) => {
    setResponses(prev => ({ ...prev, [questionId]: text }));
    setSavedMessage('');

    pendingDraftsRef.current[questionId] = text;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      draftTimerRef.current = null;
      flushDrafts();
    }, DRAFT_DEBOUNCE_MS);
  };

  const handleSave = async () => {
    setSaving(true);

    // A pending debounce must not fire after the save and resurrect a draft.
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current);
      draftTimerRef.current = null;
    }
    pendingDraftsRef.current = {};

    try {
      const promises = questions.map(q => {
        const text = responses[q.id] || '';
        return db.upsertReflectionResponse({
          questionId: q.id,
          studentId: currentUser.id,
          responseText: text
        });
      });
      const results = await Promise.all(promises);
      const failed = results.find(r => r?.error);
      if (failed) throw failed.error;

      // The server is now the source of truth for these answers.
      questions.forEach(q => clearDraft(currentUser.id, q.id));
      setDraftMessage('');

      setSavedMessage('Responses saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (err) {
      console.error('Error saving responses:', err);
      Sentry.captureException(err);
      alert(`Failed to save responses: ${err.message || err}`);
    }
    setSaving(false);
  };

  const generateMyPDF = async () => {
    try {
      // Load all questions and responses for this student
      const [allQRes, allRRes] = await Promise.all([
        db.getAllReflectionQuestions(),
        db.getStudentReflectionResponses(currentUser.id)
      ]);

      if (!allQRes.data) return;

      const allQuestions = allQRes.data;
      const allResponseMap = {};
      (allRRes.data || []).forEach(r => {
        allResponseMap[r.question_id] = r.response_text || '';
      });

      // Group questions by module_id
      const questionsByModule = {};
      allQuestions.forEach(q => {
        if (!questionsByModule[q.module_id]) questionsByModule[q.module_id] = [];
        questionsByModule[q.module_id].push(q);
      });
      Object.keys(questionsByModule).forEach(k => {
        questionsByModule[k].sort((a, b) => a.question_order - b.question_order);
      });

      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const marginVal = 20;
      const contentW = pageWidth - marginVal * 2;
      let yPos = marginVal;

      const addPageCheck = (space) => {
        if (yPos + space > pageHeight - marginVal) {
          doc.addPage();
          yPos = marginVal;
        }
      };

      // Title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('My Weekly Reflections', marginVal, yPos);
      yPos += 8;
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(currentUser.name || 'Student', marginVal, yPos);
      yPos += 5;
      if (cohort) {
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(cohort.name, marginVal, yPos);
        doc.setTextColor(0);
        yPos += 5;
      }
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(`Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`, marginVal, yPos);
      doc.setTextColor(0);
      yPos += 10;
      doc.setDrawColor(200);
      doc.line(marginVal, yPos, pageWidth - marginVal, yPos);
      yPos += 8;

      // Label each section with its week, in week order.
      const moduleIds = Object.keys(questionsByModule).sort((a, b) => {
        const wa = moduleLookup[a]?.weekNumber ?? Number.MAX_SAFE_INTEGER;
        const wb = moduleLookup[b]?.weekNumber ?? Number.MAX_SAFE_INTEGER;
        return wa - wb;
      });

      moduleIds.forEach(modId => {
        const modQuestions = questionsByModule[modId];
        if (!modQuestions || modQuestions.length === 0) return;

        const info = moduleLookup[modId];
        const heading = info?.weekNumber
          ? `Week ${info.weekNumber}${info.title ? `: ${info.title}` : ''}`
          : 'Reflections';

        addPageCheck(18);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text(heading, marginVal, yPos);
        yPos += 7;

        modQuestions.forEach((q, qi) => {
          addPageCheck(14);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          const qLines = doc.splitTextToSize(`${qi + 1}. ${q.question_text}`, contentW);
          doc.text(qLines, marginVal, yPos);
          yPos += qLines.length * 5 + 2;

          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          const respText = allResponseMap[q.id];
          if (respText) {
            const rLines = doc.splitTextToSize(respText, contentW - 5);
            doc.setTextColor(50);
            rLines.forEach(line => {
              addPageCheck(6);
              doc.text(line, marginVal + 3, yPos);
              yPos += 5;
            });
            doc.setTextColor(0);
          } else {
            doc.setTextColor(150);
            doc.text('No response submitted', marginVal + 3, yPos);
            doc.setTextColor(0);
            yPos += 5;
          }
          yPos += 5;
        });
        yPos += 5;
      });

      doc.save(`My_Reflections_${(currentUser.name || 'Student').replace(/\s+/g, '_')}.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
        <p>Loading reflections...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#b91c1c' }}>
        <p style={{ margin: '0 0 8px' }}>Could not load this week's reflections.</p>
        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>{loadError}</p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
        <p>No reflection questions for this week yet.</p>
      </div>
    );
  }

  return (
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '20px', color: '#dc2626' }}>Error loading reflections.</div>}>
      <div className="student-reflections" style={{ padding: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ margin: 0 }}>
              Weekly Reflections
            </h3>
            {moduleTitle && (
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
                Week {weekNumber}: {moduleTitle}
              </p>
            )}
          </div>
          <button className="btn-secondary" onClick={generateMyPDF} style={{ fontSize: '12px' }}>
            Download My Reflections PDF
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {questions.map((q, i) => (
            <div key={q.id} style={{
              padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#fafafa'
            }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '14px', marginBottom: '8px', color: '#374151' }}>
                {i + 1}. {q.question_text}
              </label>
              <textarea
                rows={5}
                value={responses[q.id] || ''}
                onChange={(e) => handleResponseChange(q.id, e.target.value)}
                placeholder="Type your reflection here..."
                style={{
                  width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '6px',
                  fontSize: '14px', lineHeight: '1.6', resize: 'vertical', fontFamily: 'inherit'
                }}
              />
              <div style={{ textAlign: 'right', fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                {(responses[q.id] || '').length} characters
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
          {savedMessage && (
            <span style={{ color: '#059669', fontSize: '13px', fontWeight: '500' }}>{savedMessage}</span>
          )}
          {!savedMessage && draftMessage && (
            <span style={{ color: '#6b7280', fontSize: '13px' }}>{draftMessage}</span>
          )}
          <div style={{ marginLeft: 'auto' }}>
            <button
              className="btn-primary"
              onClick={handleSave}
              disabled={saving}
              style={{ padding: '10px 24px' }}
            >
              {saving ? 'Saving...' : 'Save Responses'}
            </button>
          </div>
        </div>
      </div>
    </Sentry.ErrorBoundary>
  );
};

export default StudentReflections;
